import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { InputAdornment } from '@mui/material'
import { format as dateFormat, isDate } from 'date-fns'

import { formatDateInput } from '../../util/strings'
import { Divider, FlexColumn, FormRow } from '../styles/Containers'
import { Subheading } from '../styles/Text'
import { formSection, formWording } from '../styles/FormStyleConfig'
import FormInput from '../inputFields/FormInput'
import FormattedInput from '../inputFields/FormattedInput'
import CalendarDateButton from '../common/CalendarDateButton'
import PropertyAddresses from './PropertyAddresses'

function RequestDetailsForm({ format = 'default', perspective = 'requestor', disabled = false }) {
  const { getValues, watch, setValue } = useFormContext()
  const [currentStartDate, setCurrentStartDate] = useState()
  const [currentEndDate, setCurrentEndDate] = useState()

  const periodStart = watch('periodStart')
  const periodEnd = watch('periodEnd')

  useEffect(() => {
    if (periodStart === undefined || periodStart === null) setValue('periodStart', '')
    if (currentStartDate === undefined && periodStart !== undefined && periodStart !== null) {
      const value = getValues('periodStart')
      setCurrentStartDate(isDate(value) ? dateFormat(value, 'MMM-dd-yyyy') : value || '')
    }

    if (periodEnd === undefined || periodEnd === null) setValue('periodEnd', '')
  }, [])

  useEffect(() => {
    const value = getValues('periodStart')
    setCurrentStartDate(isDate(value) ? dateFormat(value, 'MMM-dd-yyyy') : value || dateFormat(new Date(), 'MMM-dd-yyyy'))
  }, [periodStart])

  useEffect(() => {
    const value = getValues('periodEnd')
    setCurrentEndDate(isDate(value) ? dateFormat(value, 'MMM-dd-yyyy') : value || dateFormat(new Date(), 'MMM-dd-yyyy'))
  }, [periodEnd])

  const handleConfirmStartDate = date => setValue('periodStart', dateFormat(date, 'MMM-dd-yyyy'))
  const handleConfirmEndDate = date => setValue('periodEnd', dateFormat(date, 'MMM-dd-yyyy'))

  const TitleComponent = formSection[format].font.formTitle
  const TextComponent = formSection[format].font.bodyText
  const basis = formSection[format].muiTextField.basis

  return (
    <FlexColumn gap={formSection[format].gap.div}>
      <TitleComponent>Request Details</TitleComponent>
      {formSection[format].divider && <Divider />}

      <FlexColumn gap={formSection[format].gap.small}>
        <TextComponent>What time period of record(s) are {formWording[perspective].pronoun} seeking?</TextComponent>
        <FormRow gap={formSection[format].gap.row}>
          {currentStartDate !== undefined && (
            <FormattedInput
              id="periodStart"
              label="Start Date"
              handleFormatValue={formatDateInput}
              placeholder="mmm-dd-yyyy"
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarDateButton
                      initialValue={new Date(currentStartDate)}
                      title="Start Date"
                      onConfirm={handleConfirmStartDate}
                      disabled={disabled}
                    />
                  </InputAdornment>
                )
              }}
              textFieldFormat={format}
              basis={basis}
              disabled={disabled}
            />
          )}
          {currentEndDate !== undefined && (
            <FormattedInput
              id="periodEnd"
              label="End Date"
              handleFormatValue={formatDateInput}
              placeholder="mmm-dd-yyyy"
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarDateButton
                      initialValue={new Date(currentEndDate)}
                      title="End Date"
                      onConfirm={handleConfirmEndDate}
                      disabled={disabled}
                    />
                  </InputAdornment>
                )
              }}
              textFieldFormat={format}
              basis={basis}
              disabled={disabled}
            />
          )}
        </FormRow>
      </FlexColumn>
      <FormInput
        id="requestDescription"
        label={`What records do ${formWording[perspective].pronoun} want to access?`}
        required
        multiline
        minRows={5}
        textFieldFormat={format}
        disabled={disabled}
      />
      <PropertyAddresses format={format} perspective={perspective} disabled={disabled} />
      <FlexColumn gap={formSection[format].gap.small}>
        <Subheading>File or Reference Numbers</Subheading>
        <FormInput
          id="referenceNumbers"
          label="Please list any file or reference numbers or other information (if applicable)."
          multiline
          minRows={5}
          textFieldFormat={format}
          disabled={disabled}
        />
      </FlexColumn>
    </FlexColumn>
  )
}

RequestDetailsForm.propTypes = {
  format: PropTypes.oneOf(['default', 'condensed']),
  perspective: PropTypes.oneOf(['requestor', 'staff']),
  disabled: PropTypes.bool
}

export default RequestDetailsForm
